import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { computeBadges } from '../../utils/badges';
import { getMemberName } from '../../utils/playerIdentity';
import { mergeEventSources, buildEventSummaries, withResolvedWinners } from '../../utils/eventsView';

/**
 * Badge progress for the signed-in player across every club they belong to.
 * The open club uses the live data App already has; other clubs are read once, the
 * first time `enabled` is true (the profile is opened), and kept for the session.
 */
export default function usePlayerBadges({
  enabled,
  db,
  appId,
  user,
  myClubs = [],
  selectedClubId,
  currentClubData,
  archivedSessions = [],
  events = [],
  eventSummaries = {},
  excludedClubIds = [],
}) {
  const [otherClubs, setOtherClubs] = useState({}); // clubId -> { archivedSessions, events }
  const uid = user?.uid;

  useEffect(() => {
    if (!enabled || !db || !uid) return undefined;
    const missing = myClubs.filter((c) => c?.id && c.id !== selectedClubId && !otherClubs[c.id]);
    if (missing.length === 0) return undefined;

    let cancelled = false;
    const read = async (clubId, name) => {
      const snap = await getDocs(collection(db, 'artifacts', appId, 'public', 'data', 'clubs', clubId, name));
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    };

    Promise.all(
      missing.map(async (club) => {
        try {
          const [sessions, seasonDocs, eventDocs] = await Promise.all([
            read(club.id, 'archived_sessions'),
            read(club.id, 'seasons').catch(() => []),
            read(club.id, 'events').catch(() => []),
          ]);
          const clubEvents = mergeEventSources(seasonDocs, eventDocs);
          return [club.id, {
            archivedSessions: sessions,
            events: withResolvedWinners(clubEvents, buildEventSummaries(clubEvents, sessions)),
          }];
        } catch (err) {
          console.error('Badges: could not read club', club.id, err);
          return [club.id, { archivedSessions: [], events: [] }];
        }
      })
    ).then((entries) => {
      if (!cancelled) setOtherClubs((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });

    return () => { cancelled = true; };
  }, [enabled, db, appId, uid, myClubs, selectedClubId, otherClubs]);

  return useMemo(() => {
    // Clubs switched off in Achievement settings don't count
    const excluded = new Set(excludedClubIds);
    const clubs = [];
    if (selectedClubId && !excluded.has(selectedClubId)) {
      clubs.push({
        playerName: getMemberName(currentClubData, uid) || user?.displayName,
        archivedSessions,
        events: withResolvedWinners(events, eventSummaries),
      });
    }
    myClubs.forEach((club) => {
      if (club.id === selectedClubId || excluded.has(club.id) || !otherClubs[club.id]) return;
      clubs.push({ playerName: getMemberName(club, uid) || user?.displayName, ...otherClubs[club.id] });
    });
    const loading = myClubs.some((c) => c?.id && c.id !== selectedClubId && !excluded.has(c.id) && !otherClubs[c.id]);
    // otherClubs (all clubs, including ones left out of badges) also feeds the Android widget
    return { ...computeBadges(clubs), loading, otherClubs };
  }, [selectedClubId, currentClubData, uid, user?.displayName, archivedSessions, events, eventSummaries, myClubs, otherClubs, excludedClubIds]);
}
